import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import DashboardSidebar from '../../components/navigation/DashboardSidebar';
import DashboardHeader from '../../components/navigation/DashboardHeader';
import DashboardFooter from '../../components/navigation/DashboardFooter';
import MobileMenuButton from '../../components/navigation/MobileMenuButton';
import WelcomeCardDipendente from './components/WelcomeCardDipendente';
import StatCard from './components/StatCard';
import QuickActionCard from './components/QuickActionCard';
import RecentActivityCard from './components/RecentActivityCard';

const HomeDashboardDipendente = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  
  // State za podatke
  const [userName, setUserName] = useState('...');
  const [docCount, setDocCount] = useState('0');
  const [unreadCount, setUnreadCount] = useState('0'); 
  const [monthlyHours, setMonthlyHours] = useState('0h');
  const [recentActivities, setRecentActivities] = useState([]);

  useEffect(() => {
    fetchUserDataAndStats();
  }, [t]);

  const fetchUserDataAndStats = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        // 1. Dohvatanje profila
        const { data: profile } = await supabase
          .from('profili')
          .select('nome, cognome')
          .eq('id', user.id)
          .single();
        
        let displayFullName = 'User';
        if (profile) {
          displayFullName = `${profile.nome} ${profile.cognome || ''}`;
          setUserName(profile.nome);
        }

        // 2. Aktivnost (zadnji login)
        if (user.last_sign_in_at) {
          setRecentActivities([
            {
              id: 1,
              type: 'login',
              title: t('home_dipendente.activities.login_title'),
              description: t('home_dipendente.activities.login_desc'),
              timestamp: new Date(user.last_sign_in_at),
              user: { name: displayFullName, avatar: "" }
            }
          ]);
        }

        // 3. UKUPAN BROJ DOKUMENATA (MODIFIKOVANO: samo is_deleted = false)
        const { count: dCount } = await supabase
          .from('documenti')
          .select('*', { count: 'exact', head: true })
          .eq('id_utente', user.id)
          .eq('is_deleted', false); // NOVO

        if (dCount !== null) setDocCount(dCount.toString());

        // 4. NEPROČITANI DOKUMENTI (MODIFIKOVANO: samo is_deleted = false)
        const { count: uCount } = await supabase
          .from('documenti')
          .select('*', { count: 'exact', head: true })
          .eq('id_utente', user.id)
          .eq('procitano', false)
          .eq('is_deleted', false); // NOVO

        if (uCount !== null) setUnreadCount(uCount.toString());

        // 5. Računanje sume sati
        const now = new Date();
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
        const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59).toISOString();

        const { data: timbrature, error: tError } = await supabase
          .from('timbrature')
          .select('ore_ordinarie')
          .eq('id_utente', user.id)
          .gte('data', firstDay)
          .lte('data', lastDay);

        if (!tError && timbrature) {
          const total = timbrature.reduce((acc, curr) => acc + (parseFloat(curr.ore_ordinarie) || 0), 0);
          setMonthlyHours(`${total.toFixed(1)}h`);
        }
      }
    } catch (err) {
      console.error("Greška kod dohvatanja podataka:", err.message);
    }
  };

  const statsData = [
    {
      id: 1,
      title: 'home_dipendente.stats.my_documents',
      value: docCount,
      icon: 'FileText',
      iconColor: 'var(--color-primary)',
      onClick: () => navigate('/my-documents')
    },
    {
      id: 2,
      title: 'home_dipendente.stats.monthly_hours',
      value: monthlyHours,
      icon: 'Clock',
      iconColor: 'var(--color-success)',
      onClick: () => navigate('/mio-profilo')
    },
    {
      id: 3,
      title: 'home_dipendente.stats.pending_notif',
      value: unreadCount,
      icon: 'Bell',
      iconColor: 'var(--color-warning)',
      onClick: () => navigate('/my-documents')
    },
    {
      id: 4,
      title: 'home_dipendente.stats.avg_session',
      value: '8.2h',
      icon: 'Activity',
      iconColor: 'var(--color-accent)',
      onClick: () => console.log('Tempo')
    }
  ];

  const quickActions = [
    {
      id: 1,
      title: t('home_superadmin.actions.profile.title'),
      description: t('home_superadmin.actions.profile.desc'),
      icon: 'User',
      iconColor: 'var(--color-primary)',
      onClick: () => navigate('/mio-profilo')
    },
    {
      id: 2,
      title: t('home_superadmin.actions.settings.title'),
      description: t('home_superadmin.actions.settings.desc'),
      icon: 'Settings',
      iconColor: 'var(--color-secondary)',
      onClick: () => navigate('/settings')
    },
    {
      id: 3,
      title: t('home_dipendente.actions.notifications.title'),
      description: t('home_dipendente.actions.notifications.desc'),
      icon: 'Bell',
      iconColor: 'var(--color-warning)',
      onClick: () => navigate('/my-documents')
    },
    {
      id: 4,
      title: t('home_superadmin.actions.support.title'),
      description: t('home_superadmin.actions.support.desc'),
      icon: 'HelpCircle',
      iconColor: 'var(--color-accent)',
      onClick: () => console.log('Supporto')
    }
  ];

  const handleLogout = () => navigate('/landing-authentication');
  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);
  
  return (
    <div className="min-h-screen bg-background text-foreground">
      <MobileMenuButton isOpen={isSidebarOpen} onClick={toggleSidebar} />
      <DashboardSidebar isCollapsed={!isSidebarOpen} onToggle={toggleSidebar} />
      <DashboardHeader onLogout={handleLogout} userName={userName} />

      <main className="main-content with-sidebar">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-10">
          <WelcomeCardDipendente userName={userName} />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mt-6 md:mt-8">
            {statsData.map((stat) => (
              <StatCard key={stat.id} {...stat} />
            ))}
          </div>

          <div className="mt-6 md:mt-8">
            <h2 className="text-xl md:text-2xl font-black uppercase tracking-tighter text-foreground mb-4 md:mb-6">
              {t('home_superadmin.sections.quick_actions')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              {quickActions.map((action) => (
                <QuickActionCard key={action.id} {...action} />
              ))}
            </div>
          </div>

          <div className="mt-6 md:mt-8">
            <RecentActivityCard activities={recentActivities} />
          </div>
        </div>
      </main>
      <DashboardFooter />
    </div>
  );
};

export default HomeDashboardDipendente;